import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ToastProvider } from '../bom/ui/Toast';
import SbomReleases from './SbomReleases';
import SbomComponents from './SbomComponents';
import SbomImport from './SbomImport';
import SbomVulns from './SbomVulns';
import SbomDashboard from './SbomDashboard';
import SbomBaselines from './SbomBaselines';
import SbomStudio from './SbomStudio';
import { sbomApi } from './sbomApi';
import '../bom/bom-design-system.css';
import '../styles/DocReview.css';
import './Sbom.css';

// The Software BOM's views, in nav order — sub-items under BOM, after the hardware views.
export const SBOM_VIEWS = [
  { key: 'sw-dashboard', label: 'Dashboard' },
  { key: 'sw-releases', label: 'Software releases' },
  { key: 'sw-components', label: 'Software components' },
  { key: 'sw-vulns', label: 'Vulnerabilities' },
  { key: 'sw-studio', label: 'SBOM AI Studio' },
  { key: 'sw-import', label: 'Import SBOM' },
];
// The dashboard, its baselines and the Studio share the Risk / Traceability dashboard's look (`rsk-`),
// which sizes its own text — so they render OUTSIDE `.bom-scope` (Critical Note #63).
const DASH_VIEWS = ['sw-dashboard', 'sw-baselines', 'sw-studio'];

/**
 * The Software BOM (v4.30.0) — design/DESIGN_SBOM.md, as built docs/SBOM.md.
 *
 * Its own page, settings and API, beside the hardware BOM viewer rather than inside its tree:
 * the hardware tree reads a module-level settings store and four places agree on its structure
 * (Critical Note #61), and nothing here may move that. It shares the module's look (`.bom-scope`).
 */
export default React.memo(function SbomViewer({ view, onSessionExpired }) {
  return (
    <ToastProvider>
      <SbomInner view={view} onSessionExpired={onSessionExpired} />
    </ToastProvider>
  );
});

function SbomInner({ view, onSessionExpired }) {
  const { importId, releaseId } = useParams();
  const [state, setState] = useState({ status: 'loading' });
  // App re-renders every second; read the callback through a ref (Critical Note #59).
  const expiredRef = useRef(onSessionExpired);
  expiredRef.current = onSessionExpired;
  const onAuth = useCallback((err) => {
    if (err && err.httpCode === 401) { expiredRef.current?.(); return true; }
    return false;
  }, []);

  useEffect(() => {
    let off = false;
    sbomApi.settings()
      .then((d) => { if (!off) setState({ status: d.configured ? 'ready' : 'unconfigured', info: d }); })
      .catch((e) => { if (!off && !onAuth(e)) setState({ status: 'error', error: e.message }); });
    return () => { off = true; };
  }, [onAuth]);

  if (state.status === 'loading') return <div className="bom-scope sbom-page"><div className="sbom-muted">Loading…</div></div>;
  if (state.status === 'error') {
    return <div className="bom-scope sbom-page"><div className="sbom-error" role="alert">{state.error}</div></div>;
  }
  if (state.status === 'unconfigured') {
    return (
      <div className="bom-scope sbom-page">
        <div className="sbom-state">
          <h3>The Software BOM isn&rsquo;t set up for this account yet</h3>
          <p>It needs the Orcanos project and the two work item types that hold software releases and
            software components.</p>
          {state.info?.can_edit
            ? <Link to="/settings" className="sbom-btn sbom-btn--pri">Set up in Admin → Settings</Link>
            : <p className="sbom-muted">Ask an Orcanos administrator to set it up.</p>}
        </div>
      </div>
    );
  }
  const info = state.info;
  if (DASH_VIEWS.includes(view)) {
    return (
      <>
        {view === 'sw-dashboard' && <SbomDashboard releaseId={releaseId} onAuth={onAuth} />}
        {view === 'sw-baselines' && <SbomBaselines releaseId={releaseId} onAuth={onAuth} />}
        {view === 'sw-studio' && <SbomStudio onAuth={onAuth} />}
      </>
    );
  }
  return (
    <div className="bom-scope sbom-page">
      {view === 'sw-releases' && <SbomReleases info={info} onAuth={onAuth} />}
      {view === 'sw-components' && <SbomComponents info={info} onAuth={onAuth} />}
      {view === 'sw-vulns' && <SbomVulns info={info} onAuth={onAuth} />}
      {view === 'sw-import' && <SbomImport info={info} onAuth={onAuth} importId={importId} />}
    </div>
  );
}
